import { useState } from "react";

const defaultSection = {
  companyInfo: {
    id: "",
    name: "",
    everifyNumber: "",
    representativeEverifyNumber: "",
    isParent: true,
    isCanaryRepresentative: true,
    status: 0,
  },
  contactPerson: {
    contactPersonFirstName: "",
    contactPersonLastName: "",
    contactPersonPhoneNumber: "",
    contactPersonEmail: "",
  },
  address: {
    address: "",
    city: "",
    state: "",
    zipCode: "",
    county: "",
  },
  mailingAddress: {
    mailingAddress1: "",
    mailingAddress2: "",
    mailingCity: "",
    mailingState: "",
    mailingZip: "",
  },
  businessInfo: {
    employeeCount: "",
    fein: "",
    naicsCode: "",
    doingBusinessAs: "",
    dunsNumber: "",
  },
  additionalInfo: {
    notes: "",
    companyClientID: "",
    organizationCategory: "",
    companyUEINumber: "",
    whichEmployeesEVerify: "",
    categoryBestDescribes: "",
  },
};

export function useCompanyForm() {
  const deepCopy = (obj) => JSON.parse(JSON.stringify(obj));

  const [parent, setParent] = useState(deepCopy(defaultSection));
  const [children, setChildren] = useState([]); 

  // -------------------------
  // Update Parent
  // -------------------------
  const updateParent = (section, field, value) => {
    setParent((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  // -------------------------
  // Default child structure
  // -------------------------
  const emptyChild = () => ({
    id: Date.now(),

    companyInfo: {
      id: "",
      name: "",
      everifyNumber: "",
      representativeEverifyNumber: "",
      isParent: false,
      isCanaryRepresentative: false,
      status: 0,
    },

    contactPerson: {
      contactPersonFirstName: "",
      contactPersonLastName: "",
      contactPersonPhoneNumber: "",
      contactPersonEmail: "",
    },

    address: {
      address: "",
      city: "",
      state: "",
      zipCode: "",
      county: "",
    },

    mailingAddress: {
      mailingAddress1: "",
      mailingAddress2: "",
      mailingCity: "",
      mailingState: "",
      mailingZip: "",
    },

    businessInfo: {
      employeeCount: "",
      fein: "",
      naicsCode: "",
      doingBusinessAs: "",
      dunsNumber: "",
    },

    additionalInfo: {
      notes: "",
      companyClientID: "",
      organizationCategory: "",
      companyUEINumber: "",
      whichEmployeesEVerify: "",
      categoryBestDescribes: "",
    },
  });

  // -------------------------
  // Add child
  // -------------------------
  const addChildCompany = () => {
    const child = emptyChild();
    setChildren((prev) => [...prev, child]);
    return child;
  };

  // -------------------------
  // Update child
  // -------------------------
  const updateChild = (id, section, field, value) => {
    setChildren((prev) =>
      prev.map((child) =>
        child.id === id
          ? {
              ...child,
              [section]: { ...child[section], [field]: value },
            }
          : child
      )
    );
  };

  // -------------------------
  // Remove child
  // -------------------------
  const removeChild = (id) => {
    setChildren((prev) => prev.filter((c) => c.id !== id));
  };

  // -------------------------
  // loadEditData (Parent + Children)
  // -------------------------
  const loadEditData = (company) => {
    // Load parent company
    setParent({
      companyInfo: {
        id: company.id ?? "",
        name: company.name || "",
        everifyNumber: company.everifyNumber || "",
        representativeEverifyNumber: company.representativeEverifyNumber || "",
        isParent: company.isParent ?? true,
        isCanaryRepresentative: company.isCanaryRepresentative ?? true,
        status: company.status ?? 0,
      },
      contactPerson: {
        contactPersonFirstName: company.contactPersonFirstName || "",
        contactPersonLastName: company.contactPersonLastName || "",
        contactPersonPhoneNumber: company.contactPersonPhoneNumber || "",
        contactPersonEmail: company.contactPersonEmail || "",
      },
      address: {
        address: company.address || "",
        city: company.city || "",
        state: company.state || "",
        zipCode: company.zipCode || "",
        county: company.county || "",
      },
      mailingAddress: {
        mailingAddress1: company.mailingAddress1 || "",
        mailingAddress2: company.mailingAddress2 || "",
        mailingCity: company.mailingCity || "",
        mailingState: company.mailingState || "",
        mailingZip: company.mailingZip || "",
      },
      businessInfo: {
        employeeCount: company.employeeCount || "",
        fein: company.fein || "",
        naicsCode: company.naicsCode || "",
        doingBusinessAs: company.doingBusinessAs || "",
        dunsNumber: company.dunsNumber || "",
      },
      additionalInfo: {
        notes: company.notes || "",
        companyClientID: company.companyClientID || "",
        organizationCategory: company.organizationCategory || "",
        companyUEINumber: company.companyUEINumber || "",
        whichEmployeesEVerify: company.whichEmployeesEVerify || "",
        categoryBestDescribes: company.categoryBestDescribes || "",
      },
    });

    // Load children safely
    if (company.children && Array.isArray(company.children)) {
      setChildren(
        company.children.map((child) => {
          const base = emptyChild();
          const childCompanyInfo = child.companyInfo || {};
          return {
            ...base,
            id: child.id || base.id,
            companyInfo: {
              ...base.companyInfo,
              id:
                childCompanyInfo.id ?? child.id ?? base.companyInfo.id,
              ...childCompanyInfo,
            },
            contactPerson: {
              ...base.contactPerson,
              ...child.contactPerson,
            },
            address: {
              ...base.address,
              ...child.address,
            },
            mailingAddress: {
              ...base.mailingAddress,
              ...child.mailingAddress,
            },
            businessInfo: {
              ...base.businessInfo,
              ...child.businessInfo,
            },
            additionalInfo: {
              ...base.additionalInfo,
              ...child.additionalInfo,
            },
          };
        })
      );
    }
  };

  // -------------------------
  // flatten grouped structure → API format
  // -------------------------
  const flatten = (section) => ({
    ...section.companyInfo,
    ...section.contactPerson,
    ...section.address,
    ...section.mailingAddress,
    ...section.businessInfo,
    ...section.additionalInfo,
  });

  // -------------------------
  // Get payload for API
  // -------------------------
  const getPayload = () => {
    return [
      flatten(parent),
      ...children.map((child) => flatten(child)),
    ];
  };

  return {
    parent,
    children,
    updateParent,
    addChildCompany,
    updateChild,
    removeChild,
    loadEditData,
    getPayload,
  };
}
